import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from 'react-select';
import { oneKlicks } from "../../SDK/oneclick";
import { OneKlicksFileSDK } from "../../SDK/OneKlicksFileSDK";
import decodeHtml from "../../Uitiles/decodeHtml";

const EditSpecialities = ({ closeModal, data, getSpecialities }) => {
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [manualName, setManualName] = useState(data.name);
    const [manualImage, setManualImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(data.url);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countries, setCountries] = useState([]);
    const [old_id, setOld_id] = useState(data.old_id);
    const [old_url, setOld_url] = useState(data.url);
    const [color, setColor] = useState(data.color || '#000000');

    useEffect(() => {
        fetchCountries();
        setManualName(decodeHtml(data.name));
        setOld_id(data.id);
        setColor(data.color || '#000000');
    }, []);

    const fetchCountries = async () => {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        const primaryUrl = 'https://restcountries.com/v3.1/all?fields=name,cca2,flags';
        const fallbackUrl = 'https://restcountries.com/v2/all?fields=name,alpha2Code,flag';

        const normalizeV3 = (item) => ({
            value: item?.name?.common ?? '',
            label: item?.name?.common ?? '',
            code: (item?.cca2 ?? '').toLowerCase(),
            flag: item?.flags?.png || item?.flags?.svg || ''
        });

        const normalizeV2 = (item) => ({
            value: item?.name ?? '',
            label: item?.name ?? '',
            code: (item?.alpha2Code ?? '').toLowerCase(),
            flag: item?.flag || ''
        });

        try {
            let res = await fetch(primaryUrl, { signal: controller.signal });
            if (!res.ok) throw new Error(`Primary countries API failed: ${res.status}`);
            let responseData = await res.json();
            let formatted = Array.isArray(responseData) ? responseData.map(normalizeV3) : [];
            formatted = formatted
                .filter(c => c.value && c.code)
                .sort((a, b) => a.label.localeCompare(b.label));

            if (formatted.length === 0) throw new Error('Primary countries API returned empty list');

            setCountries(formatted);
            // Set selected country to match existing data.country when available
            const match = formatted.find(c => c.value === data.country || c.label === data.country || c.code === (data.country || '').toLowerCase());
            if (match) {
                setSelectedCountry(match);
            } else {
                const india = formatted.find(c => c.label === 'India' || c.code === 'in');
                if (india) {
                    setSelectedCountry(india);
                } else if (formatted.length > 0) {
                    setSelectedCountry(formatted[0]);
                }
            }
        } catch (primaryError) {
            console.warn('Primary countries API error, trying fallback:', primaryError);
            try {
                let res2 = await fetch(fallbackUrl, { signal: controller.signal });
                if (!res2.ok) throw new Error(`Fallback countries API failed: ${res2.status}`);
                let responseData2 = await res2.json();
                let formatted2 = Array.isArray(responseData2) ? responseData2.map(normalizeV2) : [];
                formatted2 = formatted2
                    .filter(c => c.value && c.code)
                    .sort((a, b) => a.label.localeCompare(b.label));

                setCountries(formatted2);
                const match2 = formatted2.find(c => c.value === data.country || c.label === data.country || c.code === (data.country || '').toLowerCase());
                if (match2) {
                    setSelectedCountry(match2);
                } else {
                    const india = formatted2.find(c => c.label === 'India' || c.code === 'in');
                    if (india) {
                        setSelectedCountry(india);
                    } else if (formatted2.length > 0) {
                        setSelectedCountry(formatted2[0]);
                    }
                }
            } catch (fallbackError) {
                console.error('Error fetching countries (both primary and fallback failed):', fallbackError);
            }
        } finally {
            clearTimeout(timeoutId);
        }

        // Load image preview from file service using the speciality's stored URL
        try {
            const imageurl = await OneKlicksFileSDK.getfile(data.url);
            if (imageurl && imageurl.fileUrl) {
                setImagePreview(imageurl.fileUrl);
            }
        } catch (imgErr) {
            console.warn('Failed to load image preview:', imgErr);
        }
    };

    const customStyles = {
        control: (base) => ({
            ...base,
            minHeight: '50px',
            borderRadius: '4px',
            border: '1px solid #ced4da',
            boxShadow: 'none',
            '&:hover': {
                border: '1px solid #ced4da'
            }
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#e9ecef' : 'white',
            color: state.isSelected ? 'white' : 'black',
            '&:hover': {
                backgroundColor: state.isSelected ? '#0d6efd' : '#e9ecef'
            }
        })
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file && file.type.startsWith('image/') && file.size <= 2 * 1024 * 1024) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
                setManualImage(file);
            };
            reader.readAsDataURL(file);
        } else {
            alert('Please upload a valid image (max 2MB).');
        }
    };

    const handleSubmit = async () => {
        setLoading(true);
        setUploading(true);

        try {
            // Upload file first
            const root = 'specialities';
            const status = '';
            const file = manualImage;
            let uploadimage = null;
            if(file) {
                uploadimage = await OneKlicksFileSDK.uploadFile(root, status, file);
                
                if (!uploadimage || !uploadimage.fileId) {
                    throw new Error('Failed to upload image');
                }
                if (old_url) {
                    await OneKlicksFileSDK.deleteFile(old_url);
                }
            }

            setUploading(false);

            // Create speciality record
            const tableSchema = '';
            const tableName = 'specialities';
            const conditions = {
                id: old_id
            };
            const data = {
                country: selectedCountry.value,
                url: file ? uploadimage.fileId : old_url,
                name: manualName,
                color: color,
            };

            const response = await oneKlicks.update(tableSchema, tableName, data, conditions);
            
            if (response.success === true) {
                console.log("Successfully added specialization:", response.data);
                alert("Speciality updated successfully!");
                closeModal(false);
                getSpecialities();
            } else {
                throw new Error('Failed to create speciality');
            }
        } catch (error) {
            console.error("Error:", error);
            alert(error.message || "Error adding speciality. Please try again.");
        } finally {
            setLoading(false);
            setUploading(false);
        }
    };

    return (
        <div className="modal show d-block" style={{ background: '#00000040' }}>
            <div className="modal-dialog">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">Edit Speciality</h5>
                        <button type="button" className="btn-close" onClick={() => closeModal(false)}></button>
                    </div>
                    <div className="modal-body">
                        <div className="mb-3">
                            <label className="form-label">Select Country:</label>
                            <Select
                                value={selectedCountry}
                                onChange={setSelectedCountry}
                                options={countries}
                                styles={customStyles}
                                placeholder="Search for a country..."
                                isSearchable={true}
                                className="basic-single"
                                classNamePrefix="select"
                                style={{width: '100%', height: '48px', fontSize: '18px'}}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Speciality Name:</label>
                            <input
                                type="text"
                                className="form-control"
                                value={manualName}
                                onChange={(e) => setManualName(e.target.value)}
                                placeholder="Enter speciality name"
                                disabled={loading}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Color:</label>
                            <input
                                type="color"
                                className="form-control"
                                value={color}
                                onChange={(e) => setColor(e.target.value)}
                                disabled={loading}
                                style={{ height: '50px' }}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Image:</label>
                            <input
                                type="file"
                                className="form-control"
                                onChange={handleImageChange}
                                accept="image/*"
                                disabled={loading}
                            />
                            {imagePreview && (
                                <div className="mt-3">
                                    <img src={imagePreview} alt="Preview" className="img-thumbnail" style={{ width: '100px', height: '100px' }} />
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="modal-footer">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handleSubmit}
                            disabled={loading}
                        >
                            {loading ? (uploading ? 'Uploading Image...' : 'Updating Speciality...') : 'Update Speciality'}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={() => closeModal(false)} disabled={loading}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditSpecialities;
